import { memo, useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { KeyRound, RotateCcw, ShieldOff, Trash2, UserPlus } from 'lucide-react'
import { usersApi, type SecurityAuditEvent, type UserMutation } from '../../lib/authApi.js'
import { HttpError } from '../../lib/http.js'
import { useAuthUser } from '../../store/index.js'
import type { ManagedUser, Permission, UserRole } from '../../types/index.js'
import { ModalShell } from '../common/ModalShell.js'
import { Button } from '../ui/button.js'

const ALL_PERMISSIONS: Permission[] = [
  'GRID_VIEW', 'GRID_ITEM_CREATE', 'GRID_ITEM_EDIT', 'GRID_ITEM_MOVE', 'GRID_ITEM_DELETE',
  'GRID_RECURRENCE_MANAGE', 'GRID_LIFECYCLE_MANAGE', 'GRID_CLEAR', 'GRID_AI_MANAGE',
  'GRID_TEMPLATE_VIEW', 'GRID_TEMPLATE_MANAGE', 'PROGRAM_VIEW', 'PROGRAM_MANAGE',
  'PROGRAM_DELETE', 'JINGLE_VIEW', 'JINGLE_MANAGE', 'JINGLE_DELETE',
  'CATALOG_TAXONOMY_MANAGE', 'CG_VIEW', 'CG_MANAGE', 'ADS_VIEW', 'ADS_MANAGE',
  'CLOCK_VIEW', 'CLOCK_MANAGE',
  'REPORTS_VIEW_EXPORT', 'TECHNICAL_SETTINGS_MANAGE', 'PLAYOUT_CONTROL',
]

const JOURNALIST: Permission[] = [
  'GRID_VIEW', 'CLOCK_VIEW', 'PROGRAM_VIEW', 'PROGRAM_MANAGE', 'PROGRAM_DELETE', 'CATALOG_TAXONOMY_MANAGE',
]
const EDITOR: Permission[] = [...JOURNALIST, 'JINGLE_VIEW', 'JINGLE_MANAGE', 'JINGLE_DELETE', 'CG_VIEW', 'CG_MANAGE', 'CLOCK_MANAGE']
const ROLE_DEFAULTS: Record<UserRole, readonly Permission[]> = {
  ADMIN: ALL_PERMISSIONS,
  JOURNALIST,
  EDITOR,
  TECHNICIAN: ALL_PERMISSIONS,
  ADS_INFOSERVICE: ['ADS_VIEW', 'ADS_MANAGE'],
}
const VIEW_IMPLICATIONS: Partial<Record<Permission, Permission>> = {
  GRID_ITEM_CREATE: 'GRID_VIEW', GRID_ITEM_EDIT: 'GRID_VIEW', GRID_ITEM_MOVE: 'GRID_VIEW',
  GRID_ITEM_DELETE: 'GRID_VIEW', GRID_RECURRENCE_MANAGE: 'GRID_VIEW',
  GRID_LIFECYCLE_MANAGE: 'GRID_VIEW', GRID_CLEAR: 'GRID_VIEW', GRID_AI_MANAGE: 'GRID_VIEW',
  GRID_TEMPLATE_MANAGE: 'GRID_TEMPLATE_VIEW', PROGRAM_MANAGE: 'PROGRAM_VIEW',
  PROGRAM_DELETE: 'PROGRAM_VIEW', CATALOG_TAXONOMY_MANAGE: 'PROGRAM_VIEW',
  JINGLE_MANAGE: 'JINGLE_VIEW', JINGLE_DELETE: 'JINGLE_VIEW',
  CG_MANAGE: 'CG_VIEW', CLOCK_MANAGE: 'CLOCK_VIEW', ADS_MANAGE: 'ADS_VIEW', PLAYOUT_CONTROL: 'GRID_VIEW',
}
const ROLES: Array<{ id: UserRole; label: string }> = [
  { id: 'JOURNALIST', label: 'Journaliste' },
  { id: 'EDITOR', label: 'Monteur' },
  { id: 'TECHNICIAN', label: 'Technicien' },
  { id: 'ADS_INFOSERVICE', label: 'Secrétariat' },
  { id: 'ADMIN', label: 'Administrateur' },
]
const PERMISSION_GROUPS = [
  { label: 'Grille', values: ALL_PERMISSIONS.filter(value => value.startsWith('GRID_')) },
  { label: 'Programmes et éléments d’antenne', values: ALL_PERMISSIONS.filter(value => /^(PROGRAM|JINGLE|CATALOG)/.test(value)) },
  { label: 'Habillage et publicité', values: ALL_PERMISSIONS.filter(value => /^(CG|ADS)/.test(value)) },
  { label: 'Clock', values: ALL_PERMISSIONS.filter(value => value.startsWith('CLOCK_')) },
  { label: 'Pilotage', values: ALL_PERMISSIONS.filter(value => /^(REPORTS|TECHNICAL|PLAYOUT)/.test(value)) },
]

const DELETE_ERRORS: Record<string, string> = {
  SELF_DELETE_FORBIDDEN: 'Vous ne pouvez pas supprimer votre propre compte.',
  LAST_ADMIN: 'Impossible de supprimer le dernier administrateur actif.',
  NOT_FOUND: 'Ce compte n’existe plus.',
  FORBIDDEN: 'Vous n’avez pas les droits pour supprimer un compte.',
}

export const UsersAdminPanel = memo(function UsersAdminPanel() {
  const current = useAuthUser()
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<ManagedUser | 'new' | null>(null)
  const [deleting, setDeleting] = useState<ManagedUser | null>(null)
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null)
  const [error, setError] = useState('')
  const load = useCallback(async () => {
    try {
      const query = search ? `?search=${encodeURIComponent(search)}` : ''
      setUsers(await usersApi.list(query))
      setError('')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Erreur')
    }
  }, [search])
  useEffect(() => { void load() }, [load])

  const resetPassword = useCallback(async (user: ManagedUser) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${user.name} ?`)) return
    try {
      setTemporaryPassword((await usersApi.resetPassword(user.id)).temporaryPassword)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Erreur')
    }
  }, [])

  return (
    <div className="user-admin">
      <div className="user-admin__toolbar">
        <input className="tp-input" placeholder="Rechercher un compte…" value={search} onChange={event => setSearch(event.target.value)} />
        <Button onClick={() => setEditing('new')}><UserPlus />Créer un utilisateur</Button>
      </div>
      {error && <p className="auth-error" role="alert">{error}</p>}
      <div className="user-admin__table" role="table" aria-label="Utilisateurs">
        <div className="user-admin__row user-admin__row--header" role="row">
          <span role="columnheader">Utilisateur</span><span role="columnheader">Rôle</span><span role="columnheader">Chaînes</span><span role="columnheader">État</span><span role="columnheader">Actions</span>
        </div>
        {users.map(user => (
          <div className="user-admin__row" role="row" key={user.id}>
            <span role="cell"><strong>{user.name}</strong><small>@{user.username} · {user.email}</small></span>
            <span role="cell">{ROLES.find(role => role.id === user.role)?.label}</span>
            <span role="cell">{user.role === 'ADMIN' ? 'Toutes' : user.channels.map(channel => channel.name).join(', ') || 'Aucune'}</span>
            <span role="cell">{user.active ? 'Actif' : 'Désactivé'}{user.role === 'ADMIN' && <small>MFA {user.mfaEnabled ? 'actif' : 'à enrôler'}</small>}</span>
            <span role="cell" className="user-admin__actions">
              <Button size="sm" variant="outline" onClick={() => setEditing(user)}>Modifier</Button>
              <Button size="icon-sm" variant="outline" title="Réinitialiser le mot de passe" onClick={() => void resetPassword(user)}><KeyRound /></Button>
              <Button size="icon-sm" variant="outline" title="Révoquer les sessions" onClick={() => void usersApi.revokeSessions(user.id).then(load)}><ShieldOff /></Button>
              {user.id !== current?.id && (
                <Button size="icon-sm" variant="destructive" title="Supprimer définitivement le compte" onClick={() => setDeleting(user)}><Trash2 /></Button>
              )}
            </span>
          </div>
        ))}
      </div>
      {editing && <UserEditor user={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={(password) => { setEditing(null); setTemporaryPassword(password); void load() }} />}
      {deleting && <UserDeleteDialog user={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); void load() }} />}
      {temporaryPassword && (
        <ModalShell title="Mot de passe temporaire" size="narrow" onClose={() => setTemporaryPassword(null)}>
          <div className="user-admin__temporary">
            <p>Copiez ce mot de passe maintenant. Il ne sera plus affiché.</p>
            <code>{temporaryPassword}</code>
            <Button onClick={() => setTemporaryPassword(null)}>J’ai copié le mot de passe</Button>
          </div>
        </ModalShell>
      )}
    </div>
  )
})

const UserDeleteDialog = memo(function UserDeleteDialog({ user, onClose, onDeleted }: {
  user: ManagedUser
  onClose: () => void
  onDeleted: () => void
}) {
  const [confirmation, setConfirmation] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const confirmed = confirmation.trim().toLowerCase() === user.username.toLowerCase()

  const submit = useCallback(async (event: FormEvent) => {
    event.preventDefault()
    if (!confirmed || pending) return
    setPending(true); setError('')
    try {
      await usersApi.remove(user.id)
      onDeleted()
    } catch (caught) {
      const code = caught instanceof HttpError ? caught.code : ''
      setError(DELETE_ERRORS[code] ?? (caught instanceof Error ? caught.message : 'Erreur'))
      setPending(false)
    }
  }, [confirmed, onDeleted, pending, user.id])

  return (
    <ModalShell title={`Supprimer ${user.name}`} size="narrow" onClose={onClose}>
      <form className="user-editor" onSubmit={submit}>
        <p>
          La suppression est définitive : le compte, ses sessions, ses codes de récupération MFA
          et ses attributions de chaînes seront effacés. Les contenus créés et l’historique de
          sécurité sont conservés, mais ne seront plus rattachés à ce compte.
        </p>
        <p>Pour désactiver le compte sans perdre ce lien, utilisez plutôt « Modifier » puis décochez « Compte actif ».</p>
        <label>
          Saisissez l’identifiant <code>{user.username}</code> pour confirmer
          <input className="tp-input" value={confirmation} autoFocus onChange={event => setConfirmation(event.target.value)} />
        </label>
        {error && <p className="auth-error" role="alert">{error}</p>}
        <div className="user-editor__footer">
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit" variant="destructive" disabled={!confirmed || pending}>
            {pending ? 'Suppression…' : 'Supprimer définitivement'}
          </Button>
        </div>
      </form>
    </ModalShell>
  )
})

const UserEditor = memo(function UserEditor({ user, onClose, onSaved }: {
  user: ManagedUser | null
  onClose: () => void
  onSaved: (temporaryPassword: string | null) => void
}) {
  const current = useAuthUser()
  const channels = current?.channels ?? []
  const [username, setUsername] = useState(user?.username ?? '')
  const [email, setEmail] = useState(user?.email ?? '')
  const [name, setName] = useState(user?.name ?? '')
  const [role, setRole] = useState<UserRole>(user?.role ?? 'JOURNALIST')
  const [active, setActive] = useState(user?.active ?? true)
  const [channelIds, setChannelIds] = useState<string[]>(user?.channelIds ?? [])
  const [overrides, setOverrides] = useState<Partial<Record<Permission, boolean>>>(user?.permissionOverrides ?? {})
  const [generatePassword, setGeneratePassword] = useState(true)
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const effective = useMemo(() => {
    const values = new Set(ROLE_DEFAULTS[role])
    for (const [permission, value] of Object.entries(overrides)) {
      if (value) values.add(permission as Permission)
      else values.delete(permission as Permission)
    }
    for (const permission of [...values]) {
      const implied = VIEW_IMPLICATIONS[permission]
      if (implied) values.add(implied)
    }
    return values
  }, [overrides, role])

  const submit = useCallback(async (event: FormEvent) => {
    event.preventDefault(); setError('')
    const input: UserMutation = { username, email, name, role, active, channelIds, permissionOverrides: overrides }
    if (!user) {
      input.generatePassword = generatePassword
      if (!generatePassword) input.password = password
    }
    try {
      if (user) {
        await usersApi.update(user.id, input)
        onSaved(null)
      } else {
        onSaved((await usersApi.create(input)).temporaryPassword)
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Erreur')
    }
  }, [active, channelIds, email, generatePassword, name, onSaved, overrides, password, role, user, username])

  return (
    <ModalShell title={user ? `Modifier ${user.name}` : 'Nouvel utilisateur'} size="wide" onClose={onClose}>
      <form className="user-editor" onSubmit={submit}>
        <div className="user-editor__identity">
          <label>Nom<input className="tp-input" value={name} onChange={event => setName(event.target.value)} required /></label>
          <label>E-mail<input className="tp-input" type="email" value={email} onChange={event => setEmail(event.target.value)} required /></label>
          <label>Identifiant<input className="tp-input" value={username} onChange={event => setUsername(event.target.value.toLowerCase())} required /></label>
          <label>Rôle<select className="tp-input" value={role} onChange={event => { setRole(event.target.value as UserRole); setOverrides({}) }}>{ROLES.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
          <label className="user-editor__check"><input type="checkbox" checked={active} onChange={event => setActive(event.target.checked)} /> Compte actif</label>
        </div>
        {role !== 'ADMIN' && (
          <fieldset>
            <legend>Chaînes attribuées</legend>
            <div className="user-editor__channels">{channels.map(channel => <label key={channel.id}><input type="checkbox" checked={channelIds.includes(channel.id)} onChange={event => setChannelIds(values => event.target.checked ? [...values, channel.id] : values.filter(id => id !== channel.id))} />{channel.name}</label>)}</div>
          </fieldset>
        )}
        {role !== 'ADMIN' && (
          <div className="permission-editor">
            <h3>Permissions fonctionnelles</h3>
            <p>Chaque exception s’applique globalement. « Rôle » rétablit la valeur du socle.</p>
            {PERMISSION_GROUPS.map(group => (
              <section key={group.label}>
                <h4>{group.label}</h4>
                {group.values.map(permission => {
                  const roleValue = ROLE_DEFAULTS[role].includes(permission)
                  const override = overrides[permission]
                  return <div className="permission-editor__row" key={permission}>
                    <code>{permission}</code>
                    <span>Rôle : {roleValue ? 'Autorisé' : 'Refusé'}</span>
                    <select value={override === undefined ? '' : String(override)} onChange={event => setOverrides(values => {
                      const next = { ...values }
                      if (event.target.value === '') delete next[permission]
                      else next[permission] = event.target.value === 'true'
                      return next
                    })}>
                      <option value="">Réinitialiser au rôle</option><option value="true">Autoriser</option><option value="false">Refuser</option>
                    </select>
                    <strong>{effective.has(permission) ? 'Effectif : oui' : 'Effectif : non'}</strong>
                    {override !== undefined && <button type="button" title="Réinitialiser au rôle" onClick={() => setOverrides(values => { const next = { ...values }; delete next[permission]; return next })}><RotateCcw /></button>}
                  </div>
                })}
              </section>
            ))}
          </div>
        )}
        {!user && (
          <fieldset>
            <legend>Mot de passe temporaire</legend>
            <label className="user-editor__check"><input type="checkbox" checked={generatePassword} onChange={event => setGeneratePassword(event.target.checked)} /> Générer automatiquement 20 caractères</label>
            {!generatePassword && <label>Mot de passe<input className="tp-input" type="password" minLength={12} maxLength={128} value={password} onChange={event => setPassword(event.target.value)} /></label>}
          </fieldset>
        )}
        {user?.role === 'ADMIN' && user.id !== current?.id && (
          <Button type="button" variant="outline" onClick={() => void usersApi.resetMfa(user.id).then(() => onSaved(null))}>Réinitialiser le MFA</Button>
        )}
        {error && <p className="auth-error" role="alert">{error}</p>}
        <div className="user-editor__footer"><Button type="button" variant="outline" onClick={onClose}>Annuler</Button><Button type="submit">Enregistrer</Button></div>
      </form>
    </ModalShell>
  )
})

export const SecurityAuditPanel = memo(function SecurityAuditPanel() {
  const [items, setItems] = useState<SecurityAuditEvent[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [action, setAction] = useState('')
  const [outcome, setOutcome] = useState('')
  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), pageSize: '25' })
    if (action) params.set('action', action)
    if (outcome) params.set('outcome', outcome)
    const result = await usersApi.audit(`?${params}`)
    setItems(result.items); setTotal(result.total)
  }, [action, outcome, page])
  useEffect(() => { void load() }, [load])
  return (
    <div className="security-audit">
      <div className="user-admin__toolbar">
        <input className="tp-input" placeholder="Action exacte…" value={action} onChange={event => { setAction(event.target.value); setPage(1) }} />
        <select className="tp-input" value={outcome} onChange={event => { setOutcome(event.target.value); setPage(1) }}><option value="">Tous les résultats</option><option value="SUCCESS">Succès</option><option value="FAILURE">Échec</option></select>
      </div>
      <div className="user-admin__table">
        <div className="security-audit__row security-audit__row--header"><span>Date</span><span>Acteur</span><span>Action</span><span>Résultat</span><span>Cible</span><span>IP / navigateur</span></div>
        {items.map(item => <div className="security-audit__row" key={item.id}>
          <time>{new Date(item.createdAt).toLocaleString('fr-FR')}</time>
          <span>{item.actor?.name ?? item.attemptedUsername ?? '—'}</span>
          <code>{item.action}</code><span>{item.outcome}</span>
          <span>{item.target?.name ?? '—'}</span>
          <span>{item.ip ?? '—'}<small title={item.userAgent ?? ''}>{item.userAgent ?? '—'}</small></span>
        </div>)}
      </div>
      <div className="security-audit__pager"><Button variant="outline" disabled={page === 1} onClick={() => setPage(value => value - 1)}>Précédent</Button><span>Page {page} · {total} événements</span><Button variant="outline" disabled={page * 25 >= total} onClick={() => setPage(value => value + 1)}>Suivant</Button></div>
    </div>
  )
})
